import React, { useCallback, useEffect, useRef, useState } from 'react';
import httpService from "../../services/httpService";
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { Modal, Spinner } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Cropper from 'react-easy-crop';
import eventBus from "../../services/eventBus";
import { FaSlidersH, FaImages, FaTools, FaTrash } from 'react-icons/fa';
import PodcasterCancelPodcastClub from "./PodcasterCancelPodcastClub";
import SkillButton from "../clubWizard/SkillButton";

// ─── Image helpers (mirrors StepImages.jsx) ──────────────────────────────────

const getAspectRatio = (imageType) => {
    switch (imageType) {
        case 'super_wide': return 7 / 1;
        case 'wide':       return 16 / 9;
        case 'tall':       return 9 / 16;
        default:           return 16 / 9;
    }
};

const getFieldName = (imageType) => {
    switch (imageType) {
        case 'super_wide': return 'super_wide_image';
        case 'wide':       return 'wide_image';
        case 'tall':       return 'tall_image';
        default:           return 'wide_image';
    }
};

const getImageFileName = (imageType) => {
    switch (imageType) {
        case 'super_wide': return 'super-wide-image.jpg';
        case 'wide':       return 'wide-image.jpg';
        case 'tall':       return 'tall-image.jpg';
        default:           return 'wide-image.jpg';
    }
};

const getModalTitle = (imageType) => {
    switch (imageType) {
        case 'super_wide': return 'Crop Super Wide Image (7:1)';
        case 'wide':       return 'Crop Wide Image (16:9)';
        case 'tall':       return 'Crop Tall Image (9:16)';
        default:           return 'Crop Image';
    }
};

const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new window.Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (err) => reject(err));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const rotateSize = (width, height, rotation) => {
    const rotRad = (rotation * Math.PI) / 180;
    return {
        width:  Math.abs(Math.cos(rotRad) * width)  + Math.abs(Math.sin(rotRad) * height),
        height: Math.abs(Math.sin(rotRad) * width)  + Math.abs(Math.cos(rotRad) * height),
    };
};

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const rotRad = (rotation * Math.PI) / 180;
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    const croppedCanvas = document.createElement('canvas');
    const croppedCtx = croppedCanvas.getContext('2d');
    croppedCanvas.width = pixelCrop.width;
    croppedCanvas.height = pixelCrop.height;
    croppedCtx.drawImage(canvas, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);

    return new Promise((resolve, reject) => {
        croppedCanvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas is empty'));
        }, 'image/jpeg', 0.95);
    });
};

// ─── Main component ───────────────────────────────────────────────────────────

const ClubSettings = ({ clubGuid, reloadClub }) => {
    // ── Settings tab ──────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({});
    const [activeTab, setActiveTab] = useState('settings');

    // ── Skills tab ────────────────────────────────────────────────────────────
    const [skills, setSkills] = useState([]);
    const [showAddSkill, setShowAddSkill] = useState(false);
    const [newSkill, setNewSkill] = useState({ title: '', description: '' });
    const [addingSkill, setAddingSkill] = useState(false);
    const [addSkillErrors, setAddSkillErrors] = useState({});

    const fetchSkills = () => {
        httpService.get(`/dash/club/${clubGuid}/skills`)
            .then((res) => setSkills(res.data.skills || []))
            .catch((err) => console.error('Error fetching skills:', err));
    };

    const handleSkillAction = () => {
        fetchSkills();
        reloadClub();
    };

    const handleAddSkill = async () => {
        setAddingSkill(true);
        setAddSkillErrors({});
        try {
            await httpService.post(`dash/club/${clubGuid}/skills`, newSkill);
            setNewSkill({ title: '', description: '' });
            setShowAddSkill(false);
            handleSkillAction();
        } catch (err) {
            setAddSkillErrors(err.response?.data?.errors || {});
        } finally {
            setAddingSkill(false);
        }
    };

    // ── Images tab ────────────────────────────────────────────────────────────
    const [images, setImages] = useState({ super_wide_image: null, wide_image: null, tall_image: null });
    const [imageCacheBuster, setImageCacheBuster] = useState(() => Date.now());
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [currentImageType, setCurrentImageType] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageError, setImageError] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (clubGuid) fetchSettings(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clubGuid]);

    useEffect(() => {
        if (activeTab === 'skills' && clubGuid) fetchSkills();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // ── Settings handlers ─────────────────────────────────────────────────────

    const fetchSettings = (showSpinner = false) => {
        if (showSpinner) setLoading(true);
        httpService.get(`/dash/club/${clubGuid}/settings`)
            .then((res) => {
                const s = res.data.settings || {};
                setSettings(s);
                setImages({
                    super_wide_image: s.super_wide_image || null,
                    wide_image:       s.wide_image       || null,
                    tall_image:       s.tall_image        || null,
                });
            })
            .catch((err) => console.error('Error fetching settings:', err))
            .finally(() => setLoading(false));
    };

    const handleSaveSettings = (e) => {
        e.preventDefault();
        setSaving(true);
        httpService.post(`/dash/club/${clubGuid}/settings`, settings)
            .then(() => reloadClub())
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    const handleChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    // ── Image handlers ────────────────────────────────────────────────────────

    const handleImageButtonClick = (imageType) => {
        setCurrentImageType(imageType);
        fileInputRef.current?.click();
    };

    const handleImageSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result?.toString() || '');
                setShowCropModal(true);
                setCrop({ x: 0, y: 0 });
                setZoom(1);
                setRotation(0);
            });
            reader.readAsDataURL(file);
        }
    };

    const onCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleCropSave = async () => {
        if (!croppedAreaPixels) {
            eventBus.emit("toast", { type: "light", message: "Crop area not set. Please wait for the crop to complete." });
            return;
        }
        if (!imageSrc || !clubGuid) return;

        setUploadingImage(true);
        setImageError(null);
        try {
            const blob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
            const formData = new FormData();
            formData.append(getFieldName(currentImageType), blob, getImageFileName(currentImageType));

            const res = await httpService.post(`/dash/club/${clubGuid}/images`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setImages(prev => ({
                ...prev,
                ...(res.data.data?.super_wide_image && { super_wide_image: res.data.data.super_wide_image }),
                ...(res.data.data?.wide_image       && { wide_image:       res.data.data.wide_image }),
                ...(res.data.data?.tall_image       && { tall_image:       res.data.data.tall_image }),
            }));
            setImageCacheBuster(Date.now());

            reloadClub();
            handleCloseCropModal();
        } catch (err) {
            setImageError(err.response?.data?.message || 'Error uploading image');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleCloseCropModal = () => {
        setShowCropModal(false);
        setImageSrc(null);
        setCurrentImageType(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <div className="pt-3 component-ClubSettings">
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">

                {/* ── Tab 1: Club Settings ── */}
                <Tab eventKey="settings" title={<span><FaSlidersH className="me-1" />Club Settings</span>}>
                    <Form onSubmit={handleSaveSettings}>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Club Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter club name"
                                        value={settings.title || ''}
                                        onChange={(e) => handleChange('title', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={6}
                                        placeholder="Enter club description"
                                        value={settings.description || ''}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4} className="pb-3">
                                <Form.Check
                                    type="switch"
                                    id="is-public-switch"
                                    label="Make club public"
                                    checked={settings.is_public || false}
                                    onChange={(e) => handleChange('is_public', e.target.checked)}
                                />
                                <p className="small">If active this club will be available for listeners to sign up</p>
                            </Col>

                            <Col md={4} className="pb-3">
                                <Form.Check
                                    type="switch"
                                    id="allow-discussions-switch"
                                    label="Allow discussions"
                                    checked={settings.allow_discussions || false}
                                    onChange={(e) => handleChange('allow_discussions', e.target.checked)}
                                />
                                <p className="small">If active listeners will be able to create discussion topics and participate</p>
                            </Col>

                            <Col md={4} className="pb-3">
                                <Form.Check
                                    type="switch"
                                    id="use-in-processing-switch"
                                    label="Use in processing"
                                    checked={settings.use_in_processing || false}
                                    onChange={(e) => handleChange('use_in_processing', e.target.checked)}
                                />
                                <p className="small">If active title and description will be used in key takeaway generation to help AI extract more efficiently</p>
                            </Col>

                            <Col md={4} className="pb-3">
                                <Form.Check
                                    type="switch"
                                    id="accessible-by-invite-switch"
                                    label="Accessible by invite"
                                    checked={settings.accessible_by_invite || false}
                                    onChange={(e) => handleChange('accessible_by_invite', e.target.checked)}
                                />
                                <p className="small">If active this club will issue invites and listeners can use them to sign up</p>
                            </Col>

                            <Col md={4} className="pb-3">
                                <Form.Check
                                    type="switch"
                                    id="hidden-switch"
                                    label="Hidden"
                                    checked={settings.hidden || false}
                                    onChange={(e) => handleChange('hidden', e.target.checked)}
                                />
                                <p className="small">If active this club will not appear anywhere for listeners, overriding public and invite settings</p>
                            </Col>

                            <Col md={12}>
                                <div className="d-flex justify-content-end">
                                    <Button type="submit" variant="primary" disabled={saving}>
                                        {saving ? 'Saving...' : 'Save Settings'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Tab>

                {/* ── Tab 2: Club Skills ── */}
                <Tab eventKey="skills" title={<span><FaTools className="me-1" />Club Skills</span>}>
                    <div className="mt-3">
                        <div className="mb-3">
                            <Button variant="outline-primary" size="sm" onClick={() => setShowAddSkill(true)}>
                                + Add Skill
                            </Button>
                        </div>
                        {skills.length > 0 ? (
                            skills.map((skill) => (
                                <SkillButton
                                    key={skill.guid}
                                    skill={skill}
                                    podcastClubGuid={clubGuid}
                                    onSkillAction={handleSkillAction}
                                />
                            ))
                        ) : (
                            <p className="text-muted">No skills added yet.</p>
                        )}
                    </div>
                </Tab>

                {/* ── Tab 3: Club Images ── */}
                <Tab eventKey="images" title={<span><FaImages className="me-1" />Club Images</span>}>
                    {imageError && <Alert variant="danger" dismissible onClose={() => setImageError(null)}>{imageError}</Alert>}

                    <input
                        type="file"
                        ref={fileInputRef}
                        className="d-none"
                        accept="image/*"
                        onChange={handleImageSelect}
                    />

                    <Row className="mb-4">
                        <Col sm={12}>
                            <Row>
                                <Col className={'pb-3'} sm={4}>
                                    <Button variant="outline-secondary" className="w-100" onClick={() => handleImageButtonClick('super_wide')}>
                                        Upload Super Wide (7:1)
                                    </Button>
                                </Col>
                                <Col className={'pb-3'} sm={4}>
                                    <Button variant="outline-secondary" className="w-100" onClick={() => handleImageButtonClick('wide')}>
                                        Upload Wide (16:9)
                                    </Button>
                                </Col>
                                <Col className={'pb-3'} sm={4}>
                                    <Button variant="outline-secondary" className="w-100" onClick={() => handleImageButtonClick('tall')}>
                                        Upload Tall (9:16)
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                        <Col sm={12}>
                            <Row>
                                <Col sm={12} className="mb-3">
                                    <small className="text-muted d-block mb-1">Super Wide (7:1)</small>
                                    <img
                                        src={`${images.super_wide_image || '/img/club-defaults/super-wide.jpg'}?t=${imageCacheBuster}`}
                                        alt="Super wide"
                                        style={{ maxWidth: '100%', height: 'auto' }}
                                    />
                                </Col>
                                <Col sm={9} className="mb-3">
                                    <small className="text-muted d-block mb-1">Wide (16:9)</small>
                                    <img
                                        src={`${images.wide_image || '/img/club-defaults/wide.jpg'}?t=${imageCacheBuster}`}
                                        alt="Wide"
                                        style={{ maxWidth: '100%', height: 'auto' }}
                                    />
                                </Col>
                                <Col sm={3} className="mb-3">
                                    <small className="text-muted d-block mb-1">Tall (9:16)</small>
                                    <img
                                        src={`${images.tall_image || '/img/club-defaults/tall.jpg'}?t=${imageCacheBuster}`}
                                        alt="Tall"
                                        style={{ maxWidth: '100%', height: 'auto' }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Tab>

                {/* ── Tab 4: Delete ── */}
                <Tab eventKey="delete" title={<span style={{ opacity: 1 }}><FaTrash className="me-1" />Cancel</span>}>
                    {activeTab === 'delete' && (
                        <PodcasterCancelPodcastClub clubGuid={clubGuid} />
                    )}
                </Tab>
            </Tabs>

            {/* ── Add Skill Modal ── */}
            <Modal show={showAddSkill} onHide={() => setShowAddSkill(false)} size="lg" backdrop="static" keyboard={false}>
                <Modal.Body className="p-0">
                    <img
                        src="/img/studio-wide.jpg"
                        alt="Practice Skills"
                        style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: 'var(--bs-modal-inner-border-radius) var(--bs-modal-inner-border-radius) 0 0' }}
                    />
                    <div className="p-3">
                    <Row>
                        <Col sm={12}>
                            <Form.Group className="mb-3" controlId="newSkillTitle">
                                <Form.Label>Skill Name <span className="text-muted fw-normal">— What your listener will get sharper at by following the club. They see this when they join, and they tag it later when they actually try it out in their week.</span></Form.Label>
                                <Form.Control
                                    name="title"
                                    value={newSkill.title}
                                    type="text"
                                    placeholder="Example: Confident decision-making"
                                    onChange={(e) => setNewSkill(prev => ({ ...prev, title: e.target.value }))}
                                    isInvalid={!!addSkillErrors.title}
                                    disabled={addingSkill}
                                />
                                <Form.Control.Feedback type="invalid">{addSkillErrors.title ?? ''}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col sm={12}>
                            <Form.Group className="mb-0" controlId="newSkillDescription">
                                <Form.Label>What does this skill involve? <span className="text-muted fw-normal">— What trying this skill out looks like in practice. The clearer this is, the easier it is for a listener to recognise the moment to tag, and for Podsy to spot the skill in your episodes.</span></Form.Label>
                                <Form.Control
                                    name="description"
                                    value={newSkill.description}
                                    as="textarea"
                                    rows={3}
                                    placeholder="A short sentence describing how listeners will practise or apply this skill"
                                    onChange={(e) => setNewSkill(prev => ({ ...prev, description: e.target.value }))}
                                    isInvalid={!!addSkillErrors.description}
                                    disabled={addingSkill}
                                />
                                <Form.Control.Feedback type="invalid">{addSkillErrors.description ?? ''}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddSkill(false)} disabled={addingSkill}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddSkill} disabled={addingSkill}>
                        {addingSkill ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</> : 'Add Skill'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Crop Modal ── */}
            <Modal show={showCropModal} onHide={handleCloseCropModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{getModalTitle(currentImageType)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ position: 'relative', width: '100%', height: 400, backgroundColor: '#333' }}>
                        {imageSrc && (
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={getAspectRatio(currentImageType)}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                onRotationChange={setRotation}
                            />
                        )}
                    </div>
                    <div className="mt-3">
                        <Form.Label>Zoom: {zoom.toFixed(1)}x</Form.Label>
                        <Form.Range value={zoom} min={1} max={3} step={0.1} onChange={(e) => setZoom(parseFloat(e.target.value))} />
                    </div>
                    <div className="mt-3">
                        <Form.Label>Rotation: {rotation}°</Form.Label>
                        <Form.Range value={rotation} min={0} max={360} step={1} onChange={(e) => setRotation(parseInt(e.target.value))} />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCropModal} disabled={uploadingImage}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCropSave} disabled={uploadingImage}>
                        {uploadingImage ? (
                            <><Spinner as="span" animation="border" size="sm" className="me-2" />Uploading…</>
                        ) : 'Save & Upload'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ClubSettings;
